/**
 * integrations domain (doc 01 A5.1 Records & sync; doc 10.3).
 *
 * The customer-facing Integration Center client. Function names + paths mirror
 * the backend route inventory verbatim. Every shape here is PRODUCT-LEVEL: the
 * server projects the internal state machine and sync-run telemetry onto a small
 * closed vocabulary before it crosses the wire (owner #5050 — no sync-engine
 * telemetry, no raw vendor bodies).
 *
 * SECRETS (doc 01 A5.1; ADR 0013): credential values are WRITE-ONLY. They are
 * submitted through {@link storeCredentials} / {@link rotateCredentials} and are
 * NEVER returned by any read path — `credentialSchema` carries field NAMES and
 * flags only, and no response ever includes a credential value or ref.
 */
import { get, post } from './transport.js';
/**
 * `GET /integrations/connectors` → `{ connectors }` (needs `integrations:read`).
 * The Integration Center's read surface: every defined connector with its
 * product-level state and credential form schema.
 */
export async function connectors(options) {
    const body = await get('/integrations/connectors', options);
    return body.connectors;
}
/**
 * `POST /integrations` → `{ integration }` (needs `integrations:manage`). Creates
 * the instance with its non-secret config. Storing a credential is a separate,
 * write-only step; creating never proves the connection works.
 */
export async function create(kind, config, options) {
    const body = await post('/integrations', { kind, ...(config ? { config } : {}) }, options);
    return body.integration;
}
/**
 * `POST /integrations/:id/credentials` → `{ integration }` (needs
 * `integrations:manage`). Write-only: the secret goes to Secret Manager; no
 * response ever returns it.
 */
export async function storeCredentials(id, input, options) {
    const body = await post(`/integrations/${id}/credentials`, input, options);
    return body.integration;
}
/**
 * `POST /integrations/:id/rotate-credentials` → `{ integration }` (needs
 * `integrations:manage`). Replaces the stored secret; write-only like store.
 */
export async function rotateCredentials(id, input, options) {
    const body = await post(`/integrations/${id}/rotate-credentials`, input, options);
    return body.integration;
}
/**
 * `POST /integrations/:id/test` → `ConnectionTestResult` (needs
 * `integrations:manage`). Runs a server-side connection test with the stored
 * credential; the result carries a machine-safe code + connector copy only.
 */
export async function test(id, options) {
    return post(`/integrations/${id}/test`, undefined, options);
}
/** `POST /integrations/:id/enable` → `{ integration }` (needs `integrations:manage`). */
export async function enable(id, options) {
    const body = await post(`/integrations/${id}/enable`, undefined, options);
    return body.integration;
}
/** `POST /integrations/:id/disable` → `{ integration }` (needs `integrations:manage`). */
export async function disable(id, options) {
    const body = await post(`/integrations/${id}/disable`, undefined, options);
    return body.integration;
}
/**
 * `POST /integrations/:id/sync` → 202 `{ queued, integration }` (needs
 * `integrations:manage`). ENQUEUES one incremental (or explicitly ranged) sync
 * as a durable job — the server never runs the vendor walk inside this
 * request. A 409 means an import is already running (the response carries
 * `runId`/`startedAt`, the visible run-state); a 503 means the durable sync
 * engine is not wired on this deployment.
 */
export async function sync(id, input, options) {
    return post(`/integrations/${id}/sync`, input, options);
}
/**
 * `POST /integrations/:id/connect-sync` → 202 `{ queued, integration }` (needs
 * `integrations:manage`). Kicks off the FULL first-sync lifecycle as a durable
 * job: historical backfill → reconciliation → historical-complete marker +
 * initial checkpoint → recurring sync activation. `sourceHistoryStart` is
 * optional — the API falls back to the org's configured start, then the pilot
 * default.
 */
export async function connectSync(id, input, options) {
    return post(`/integrations/${id}/connect-sync`, input, options);
}
/**
 * `POST /integrations/:id/disconnect` → `{ integration }` (needs
 * `integrations:manage`). Destroys the stored secret and clears the connection;
 * imported records are never deleted.
 */
export async function disconnect(id, options) {
    const body = await post(`/integrations/${id}/disconnect`, undefined, options);
    return body.integration;
}
/**
 * `GET /integrations/:id/history` → `{ history }` (needs `integrations:read`).
 * The real, read-only import history (replaces the former `not_yet_available`
 * run-detail sentinel). Product-level entries only — no sync-engine telemetry.
 */
export async function history(id, options) {
    const body = await get(`/integrations/${id}/history`, options);
    return body.history;
}
/**
 * `GET /integrations/:id/streams` → `{ streams }` (needs `integrations:read`).
 *
 * Per-stream freshness and completeness — see {@link StreamState}. This is
 * the answer PH-ADV-005 asked for: a run can report a healthy overall history
 * while ONE declared stream sits at `completeness: 'not_attempted'`, and
 * `history()` above never carries which stream. Render this list beside the
 * connector's status pill so "Synced" is never the only thing an operator can
 * see.
 */
export async function streams(id, options) {
    const body = await get(`/integrations/${id}/streams`, options);
    return body.streams;
}
/**
 * `GET /integrations/:id/history/:runId/rejected-records` → `{ rejected }`
 * (needs `integrations:manage`, NOT the `integrations:read` the rest of this
 * module takes — see {@link RejectedRecordReport} for why).
 *
 * WHICH records an import left behind. {@link SyncHistoryEntry.recordsRejected}
 * says how many; this says which, and is the reason the count is actionable at
 * all. A run belonging to another integration is a 404, not a refusal.
 *
 * The response carries vendor record ids. Callers render them and do nothing
 * else with them.
 */
export async function rejectedRecords(id, runId, options) {
    const body = await get(`/integrations/${id}/history/${runId}/rejected-records`, options);
    return body.rejected;
}
//# sourceMappingURL=integrations.js.map